import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { BiShow, BiHide } from "react-icons/bi";
import styled from "styled-components";
import logo_rounded from "../../assets/logo_rounded.png";
import { useAuth } from "../context/AuthContext";
import { resendOTP } from "../redux/slice/resendOTP";
import {
  AuthCard,
  AuthPanel,
  BrandContainer,
  Container,
  ErrorMessage,
  InputContainer,
  InputField,
  InputLabel,
  LogoImage,
  SubmitButton,
  Subtitle,
  Title,
  VisualContent,
  VisualPanel,
  SuccessMessage,
  FloatingShapes,
  Circle,
  Triangle,
  MainHeading,
  SubHeading,
  FeatureList,
  FeatureItem,
  FeatureIcon,
    ClockFace,
  ClockCenter,
  Hand,
  HourHand,
  MinuteHand,
  SecondHand,
  Tick
} from "./loginStyledComp";

const OtpContainer = styled.div`
  margin: 1.5rem 0;
`;

const OtpTitle = styled.h4`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const OtpSubtitle = styled.p`
  font-size: 0.7rem;
  color: #666;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const OtpInputsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const OtpInput = styled.input`
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 1.2rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const OtpFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 1.2rem;
  color: #666;
`;

const OtpResendButton = styled.button`
  background: none;
  border: none;
  color: #1890ff;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const OtpTimer = styled.span`
  color: #ff4d4f;
  font-weight: 500;
`;

const customData = require("../../package.json");

const Login = () => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { onLogin, onVerifyOtp } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timeLeft, setTimeLeft] = useState(180);
  const [canResend, setCanResend] = useState(false);
   const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000); 

    return () => clearInterval(interval);
  }, []);

  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  const hourRotation = (hours * 30) + (minutes * 0.5);  
  const minuteRotation = (minutes * 6) + (seconds * 0.1); 
  const secondRotation = seconds * 6; 


  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Add this useEffect hook near your other useEffect hooks
useEffect(() => {
  if (otpSent) {
    document.getElementById('otp-0')?.focus();
  }
}, [otpSent]); 

  const isIncognitoMode = () => {
    return new Promise((resolve) => {
      const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
      if (!fs) {
        resolve(false);
      } else {
        fs(
          window.TEMPORARY,
          100,
          () => resolve(false),
          () => resolve(true)
        );
      }
    });
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    setError("");
    setSuccess("");

    try {
      const isIncognito = await isIncognitoMode();
      const result = await onLogin(username, password);

      if (result.error) {
        setError(
          typeof result.error === "object" ? result.error.message : result.error
        );
      } else if (result.otp_required) {
        setOtpSent(true);
        setSuccess("OTP has been sent to your registered email");
        setTimeLeft(180);
        setCanResend(false);
      }
    } catch (error) {
      setError(error?.message || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    setError("");
    setSuccess("");
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const handleVerifyOtp = async () => {
    setVerifyLoading(true);
    setError("");
    setSuccess("");

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError("Please enter a complete 6-digit OTP code");
      setVerifyLoading(false);
      return;
    }

    try {
      const result = await onVerifyOtp(username, otpCode);

      if (result?.message === "Invalid OTP") {
        setError("Invalid OTP");
      } else if (
        result?.message ===
        "Your account is locked as you have entered wrong otp multiple times. Please contact IT support team"
      ) {
        setError(
          "Your account is locked as you have entered wrong otp multiple times. Please contact IT support team"
        );
      } else if (result?.message === "Your OTP has expired.") {
        setError("Your OTP has expired. Please resend OTP.");
      }
    } catch (error) {
      setError(error?.message || "OTP verification failed");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    try {
      setResendLoading(true);
      setError("");
      setSuccess("");

      const requestBody = { username };
      const result = await dispatch(resendOTP({ requestBody }));

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess("New OTP has been sent successfully");
        setTimeLeft(180);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        document.getElementById("otp-0").focus();
      }
    } catch (error) {
      setError(error?.message || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sign-in NO LONGER waits on geolocation.
    //
    // It used to call getCurrentPosition and only log in from the success callback, with
    // the error branch showing "Please enable location services to continue". That made
    // login impossible: the server sends `Permissions-Policy: geolocation=()` (an empty
    // allowlist, added as a VAPT remediation), so the browser refuses the request outright
    // without ever prompting — the error branch fired every time and no amount of enabling
    // location in the browser could help, because it is a policy block, not a permission.
    //
    // Nothing was lost by removing it: the success callback took no arguments and no
    // latitude/longitude was read anywhere on this screen, so the call gated login while
    // collecting nothing.
    //
    // If capturing the login location is genuinely wanted, it needs BOTH halves: the
    // Permissions-Policy header relaxed to `geolocation=(self)` AND the coordinates
    // actually read and sent. Until then, do not reintroduce a blocking call here.
    //
    // handleLogin / handleVerifyOtp each own their loading flag, so this no longer sets
    // one — the old version set loginLoading and then returned early on the
    // "not supported" path without clearing it, leaving the button spinning for ever.
    if (otpSent) {
      await handleVerifyOtp();
    } else {
      await handleLogin();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container>
      <AuthPanel>
        <AuthCard>
          <BrandContainer>
            <LogoImage src={logo_rounded} alt="Company Logo" />
          </BrandContainer>

          <Title>Sign In</Title>
          {otpSent ? (
            <Subtitle>Enter your OTP to continue</Subtitle>
          ) : (
            <Subtitle>Enter your credentials to continue</Subtitle>
          )}

          <form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            {!otpSent ? (
              <>
                <InputContainer>
                  <InputLabel>Employee ID</InputLabel>
                  <InputField
                    prefix={
                      <UserOutlined style={{ color: "rgba(0,0,0,0.25)" }} />
                    }
                    placeholder="Enter your employee ID"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError("");
                    }}
                  />
                </InputContainer>

                <InputContainer>
                  <InputLabel>Password</InputLabel>
                  <div style={{ position: "relative" }}>
                    <InputField
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      autoComplete="current-password"
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      
                      onClick={togglePasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#666",
                      }}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <BiHide size={18} />
                      ) : (
                        <BiShow size={18} />
                      )}
                    </button>
                  </div>
                </InputContainer>
              </>
            ) : (
              <OtpContainer>
                <OtpInputsContainer>
                  {otp.map((digit, index) => (
                    <OtpInput
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      pattern="\d*"
                      inputMode="numeric"
                    />
                  ))}
                </OtpInputsContainer>

                <OtpFooter>
                  <span>Didn't receive code?</span>
                  {canResend ? (
                    <OtpResendButton
                      onClick={handleResendOtp}
                      disabled={resendLoading}
                    >
                      {resendLoading ? "Sending..." : "Resend OTP"}
                    </OtpResendButton>
                  ) : (
                    <span>
                      Resend in <OtpTimer>{timeLeft}s</OtpTimer>
                    </span>
                  )}
                </OtpFooter>
              </OtpContainer>
            )}

            <SubmitButton
              type="primary"
              htmlType="submit"
              loading={otpSent ? verifyLoading : loginLoading}
            >
              {otpSent
                ? verifyLoading
                  ? "Verifying..."
                  : "Verify OTP"
                : loginLoading
                ? "Signing In..."
                : "Sign In"}
            </SubmitButton>
          </form>

          <p
            style={{
              textAlign: "center",
              marginTop: "2rem",
              color: "#adb5bd",
              fontSize: "1rem",
            }}
          >
            Version: {customData.version}
          </p>
        </AuthCard>
      </AuthPanel>

      <VisualPanel>
        <FloatingShapes>
          <Circle
            style={{ width: "150px", height: "150px", top: "10%", left: "15%" }}
          />
          <Circle
            style={{
              width: "200px",
              height: "200px",
              bottom: "15%",
              right: "10%",
            }}
          />
          <Circle
            style={{ width: "100px", height: "100px", top: "60%", left: "25%" }}
          />
          <Triangle
            style={{ top: "20%", right: "20%", transform: "rotate(45deg)" }}
          />
          <Triangle
            style={{ bottom: "25%", left: "20%", transform: "rotate(-30deg)" }}
          />
        </FloatingShapes>

        <VisualContent>

         

                  <ClockFace>
      <ClockCenter />
      <HourHand rotation={hourRotation} />
      <MinuteHand rotation={minuteRotation} />
      <SecondHand rotation={secondRotation} />
    </ClockFace>

          <MainHeading>Welcome To Zielium</MainHeading>
          <SubHeading>
            Sign in to access your personalized reports and manage your
            account.
          </SubHeading>
        </VisualContent>
      </VisualPanel>
    </Container>
  );
};

export default Login;
